import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { request } from '@/lib/utils';
import { toast } from 'sonner';

const StyledFooter = styled.div`
  background-color: #fff;
  padding: 40px 80px 40px;

  @media (max-width: 1024px) {
    padding: 40px 40px;
  }

  @media (max-width: 768px) {
    padding: 32px 24px;
  }

  @media (max-width: 480px) {
    padding: 24px 16px;
  }
`;

const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 60px;
  padding-bottom: 60px;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    gap: 40px;
    padding-bottom: 40px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr 1fr;
    gap: 32px;
    padding-bottom: 32px;
  }

  @media (max-width: 480px) {
    gap: 28px;
    padding-bottom: 28px;
  }
`;

const BrandColumn = styled.div`
  display: flex;
  align-items: flex-start;

  @media (max-width: 1024px) {
    grid-column: 1 / -1;
  }
`;

const Logo = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: #000;

  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const ColumnTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin: 0 0 20px 0;

  @media (max-width: 768px) {
    margin: 0 0 16px 0;
  }
`;

const ColumnLink = styled(Link)`
  font-size: 12px;
  color: #6b7280;
  text-decoration: none;
  margin-bottom: 12px;
  display: flex;
  align-items: center;

  transition: color 0.2s;

  &:hover {
    color: #000;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 10px;
  }
`;

const NewsletterSection = styled.div`
  padding: 60px 0 40px;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    padding: 40px 0 32px;
  }

  @media (max-width: 480px) {
    padding: 32px 0 24px;
  }
`;

const NewsletterTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #000;
  margin: 0 0 12px 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 17px;
  }
`;

const NewsletterDescription = styled.p`
  font-size: 15px;
  color: #6b7280;
  margin: 0 0 24px 0;

  @media (max-width: 768px) {
    font-size: 14px;
    margin: 0 0 20px 0;
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 12px;
  max-width: 500px;

  @media (max-width: 768px) {
    max-width: 100%;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 15px;
  outline: none;

  &:focus {
    border-color: #2563eb;
  }

  &::placeholder {
    color: #9ca3af;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 11px 14px;
  }

  @media (max-width: 480px) {
    width: calc(100% - 32px);
  }
`;

const SubscribeButton = styled.button`
  padding: 12px 32px;
  background-color: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #1d4ed8;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 11px 28px;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 12px 24px;
  }
`;

const BottomSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 24px;
    padding-top: 24px;
    align-items: flex-start;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const Copyright = styled.div`
  font-size: 14px;
  color: #4b5563;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 20px;
  }

  @media (max-width: 480px) {
    gap: 18px;
  }
`;

const SocialIcon = styled.a`
  color: #6b7280;
  font-size: 20px;
  transition: color 0.2s;

  &:hover {
    color: #000;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
`;

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await request(`/marketing/subscribe`, 'POST', { email });

      setStatus('success');
      toast.success(response.message);

    } catch (error) {
      console.log(error);
      setStatus('error');
      toast.error('An error occurred. Please try again later.');
    }
  };

  return (
    <StyledFooter>
      <FooterContent>
        <TopSection>
          <BrandColumn>
            <Logo>AXION</Logo>
          </BrandColumn>

          <Column>
            <ColumnTitle>Market Data</ColumnTitle>
            <ColumnLink to="/products/stocks">Equities</ColumnLink>
            <ColumnLink to="/products/forex">Currencies</ColumnLink>
            <ColumnLink to="/products/etfs">ETFs</ColumnLink>
            <ColumnLink to="/products/disclosures">Financials &amp; Filings</ColumnLink>
            <ColumnLink to="/products/earnings">Earnings</ColumnLink>
            <ColumnLink to="/products/futures">Futures</ColumnLink>
            <ColumnLink to="/products/indices">Indices</ColumnLink>
          </Column>

          <Column>
            <ColumnTitle>Alternative Data</ColumnTitle>
            <ColumnLink to="/products/credit">Credit</ColumnLink>
            <ColumnLink to="/products/supply-chain">Supply Chain</ColumnLink>
            <ColumnLink to="/products/esg">ESG</ColumnLink>
            <ColumnLink to="/products/sentiment">Sentiment</ColumnLink>
            <ColumnLink to="/products/news">News</ColumnLink>
            <ColumnLink to="/products/webtraffic">Web Traffic</ColumnLink>
            <ColumnLink to="/products/economics">Economics</ColumnLink>
          </Column>

          <Column>
            <ColumnTitle>Developers</ColumnTitle>
            <ColumnLink to="/developers">Quick Start Guide</ColumnLink>
            <ColumnLink to="/docs">Documentation</ColumnLink>
            <ColumnLink to="/dashboard/api-keys">API Keys</ColumnLink>
            <ColumnLink to="/developers/libraries">Client Libraries</ColumnLink>
            <ColumnLink to="/dashboard/playground">Interactive Examples</ColumnLink>
            <ColumnLink to="/blog">Tutorials &amp; Guides</ColumnLink>
          </Column>

          <Column>
            <ColumnTitle>Company</ColumnTitle>
            <ColumnLink to="/about">About Axion</ColumnLink>
            <ColumnLink to="/blog">Blog</ColumnLink>
            <ColumnLink to="/jobs">Careers</ColumnLink>
            <ColumnLink to="/support">Support</ColumnLink>
            <ColumnLink to="/contact">Contact</ColumnLink>
          </Column>

          <Column>
            <ColumnTitle>Reports</ColumnTitle>
            <ColumnLink to="/stock/AAPL">Apple (AAPL)</ColumnLink>
            <ColumnLink to="/stock/MSFT">Microsoft (MSFT)</ColumnLink>
            <ColumnLink to="/stock/NVDA">NVIDIA (NVDA)</ColumnLink>
            <ColumnLink to="/stock/TSLA">Tesla (TSLA)</ColumnLink>
            <ColumnLink to="/stock/AMZN">Amazon (AMZN)</ColumnLink>
            <ColumnLink to="/stock/META">Meta (META)</ColumnLink>
            <ColumnLink to="/stocks">View all</ColumnLink>
          </Column>

          <Column>
            <ColumnTitle>Legal</ColumnTitle>
            <ColumnLink to="/sitemap">Site Map</ColumnLink>
            <ColumnLink to="/privacy">Privacy</ColumnLink>
            <ColumnLink to="/terms">Terms</ColumnLink>
          </Column>
        </TopSection>

        <NewsletterSection>
          <NewsletterTitle>Subscribe to our newsletter</NewsletterTitle>
          <NewsletterDescription>
            The latest on our financial data API, market data, fundamentals, and quantitative research, sent to your inbox.
          </NewsletterDescription>
          <NewsletterForm onSubmit={handleSubscribe}>
            <EmailInput
              type="email"
              name="email"
              autoComplete="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <SubscribeButton type="submit" disabled={status === 'loading'}>
              {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
            </SubscribeButton>
          </NewsletterForm>

        </NewsletterSection>

        <BottomSection>
          <Copyright>© axionquant.com, Inc.</Copyright>
          <SocialLinks>

            <SocialIcon href="https://medium.com/@axionquant" aria-label="Medium" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" viewBox="0 -1.5 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"> <g id="Page-1" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd"> <g id="Dribbble-Light-Preview" transform="translate(-260.000000, -7520.000000)" fill="currentColor"> <g id="icons" transform="translate(56.000000, 160.000000)"> <path d="M212,7365.33318 L212,7371.20376 L217.333,7373.81607 L212,7365.33318 Z M223.856,7363.13685 L217.519,7360.03908 C217.416,7359.98525 217.285,7360.02538 217.223,7360.12032 L213.56,7365.94784 L217.919,7372.8794 L223.917,7363.34532 C223.966,7363.27094 223.931,7363.17698 223.856,7363.13685 Z M218.232,7374.24868 L223.45,7376.79932 C223.752,7376.94809 224,7376.79932 224,7376.46262 L224,7365.08066 L218.232,7374.24868 Z M211,7362.95089 L211,7376.46948 C211,7376.79932 210.586,7377.11056 210.283,7376.96179 L204,7373.71721 L204,7360.34935 C204,7360.12619 204.229,7359.87269 204.635,7360.07236 L211,7362.95089 Z" id="medium-[#149]"> </path> </g> </g> </g> </svg>
              <span className="sr-only">Medium</span>
            </SocialIcon>

            <SocialIcon href="https://www.instagram.com/axionquant/" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                <span className="sr-only">Instagram</span>
              </svg>
            </SocialIcon>
            <SocialIcon href="https://x.com/axionQuant" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  <span className="sr-only">X / Twitter</span>
              </svg>
            </SocialIcon>
            <SocialIcon href="https://github.com/axionquant" aria-label="GitHub" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
                <span className="sr-only">Github</span>
              </svg>
            </SocialIcon>
          </SocialLinks>
        </BottomSection>
      </FooterContent>
    </StyledFooter>
  );
};

export default Footer;
